// src/components/EditRegisterModal.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { handlePriceInput } from "../utils/inputUtils";
import { ToastContainer, toast } from "react-toastify";

const API_BASE = process.env.REACT_APP_API || "";
const PIN_PHONE = "984747662";

/* ===================== Helpers de montos ===================== */
const parseMoney = (v) => {
    if (v == null) return 0;
    const s = String(v).replace(/[^\d.,]/g, "").replace(/,/g, "");
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v) => {
    const n = parseMoney(v);
    const fixed = n.toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${intWithSep}.${decPart}`;
};

/* ===================== Validaciones de FM y Pago Online ===================== */
function validateMoneyLogic(form) {
    const errors = [];

    const fmVal = parseMoney(form.fmAmount);
    const onlineVal = parseMoney(form.onlineAmount);

    const fmStr = String(form.fm ?? "").trim();
    const onlinePayStr = String(form.onlinePay ?? "").trim();

    if (fmVal > 0 && fmStr !== "1") {
        errors.push("Debe seleccionar 'Sí' en FM si el Monto FM es mayor a 0.");
    }

    if (fmStr === "1" && fmVal <= 0) {
        errors.push("Debe ingresar un Monto FM mayor a 0 cuando FM es 'Sí'.");
    }

    if (
        onlineVal > 0 &&
        (!onlinePayStr || onlinePayStr === "0" || onlinePayStr === "")
    ) {
        errors.push(
            "Debe seleccionar un método de Pago Online (Yape, Tarjeta, Transferencia o Culqi POS) cuando el Monto Online es mayor a 0."
        );
    }

    if (
        onlinePayStr &&
        onlinePayStr !== "0" &&
        onlinePayStr !== "" &&
        onlineVal <= 0
    ) {
        errors.push(
            "Debe ingresar un Monto Online mayor a 0 cuando selecciona un método de Pago Online."
        );
    }

    return errors;
}

function EditRegisterModal({ open, data, phoneOptions, onClose, onSaved }) {
    const safeData = data || {};

    const [form, setForm] = useState({
        description: "",
        phone: "",
        phonePrice: "",
        name: "",
        paternalSur: "",
        maternalSur: "",
        fm: "",
        fmAmount: "",
        imei: "",
        dni: "",
        email: "",
        phoneNumber: "",
        cashAmount: "",
        onlinePay: "",
        onlineAmount: "",
    });

    const [reason, setReason] = useState("");
    const [smsCode, setSmsCode] = useState("");

    const [selectedPhone, setSelectedPhone] = useState(null);

    const [sendingPin, setSendingPin] = useState(false);
    const [pinSent, setPinSent] = useState(false);
    const [saving, setSaving] = useState(false);

    const catStr = String(safeData.cat ?? "");
    const subCatStr = String(safeData.subCat ?? "");
    const segmentStr = String(safeData.segment ?? "");

    // ✅ Pago de cuota (cat 2 / subCat 2)
    const isCuotaPago = catStr === "2" && subCatStr === "2";

    // ✅ Requerimiento: ocultar estas 3 columnas en pago de cuota
    const hideEquipoCelular = isCuotaPago;
    const hideFM = isCuotaPago;
    const hideMontoFM = isCuotaPago;

    const segmentSet =
        segmentStr === "1"
            ? "Payjoy"
            : segmentStr === "2"
                ? "MovilesPay"
                : segmentStr === "3"
                    ? "Efectiva"
                    : segmentStr
                        ? "MovilPro"
                        : "";

    const classNameSet =
        catStr === "1" && subCatStr === "1" && segmentStr === "1"
            ? "1"
            : catStr === "1" && subCatStr === "1" && segmentStr === "2"
                ? "2"
                : catStr === "1" && subCatStr === "1" && segmentStr === "3"
                    ? "3"
                    : catStr === "2" && subCatStr === "1"
                        ? "4"
                        : catStr === "2" && subCatStr === "2" && segmentStr === "1"
                            ? "6"
                            : catStr === "2" && subCatStr === "2" && segmentStr === "2"
                                ? "5"
                                : catStr === "2" && subCatStr === "3"
                                    ? "8"
                                    : catStr === "2" && subCatStr === "4"
                                        ? "7"
                                        : catStr === "2" && subCatStr === "5"
                                            ? "9"
                                            : catStr === "2" && subCatStr === "6"
                                                ? "11"
                                                : catStr === "3"
                                                    ? "10"
                                                    : catStr === "4"
                                                        ? "12"
                                                        : catStr === "5"
                                                            ? "13"
                                                            : "";

    useEffect(() => {
        if (open && data) {
            setForm({
                description: safeData.description || "",
                phone: safeData.phone || "",
                phonePrice: formatMoney(safeData.phonePrice || 0),
                name: safeData.name || "",
                paternalSur: safeData.paternalSur || "",
                maternalSur: safeData.maternalSur || "",
                fm: safeData.fm != null ? String(safeData.fm) : "0",
                fmAmount: formatMoney(safeData.fmAmount || 0),
                imei: safeData.imei || "",
                dni: safeData.dni || "",
                email: safeData.email || "",
                phoneNumber: safeData.phoneNumber || "",
                cashAmount: formatMoney(safeData.cashAmount || 0),
                onlinePay: safeData.onlinePay != null ? String(safeData.onlinePay) : "",
                onlineAmount: formatMoney(safeData.onlineAmount || 0),
            });

            let initialOption = null;
            if (phoneOptions && phoneOptions.length) {
                const phoneTrim = (safeData.phone || "").trim().toLowerCase();
                initialOption =
                    phoneOptions.find(
                        (opt) =>
                            String(opt.value).toLowerCase() ===
                            String(safeData.phone || "").toLowerCase() ||
                            String(opt.label).toLowerCase() === phoneTrim
                    ) || null;
            }
            setSelectedPhone(initialOption);

            setReason("");
            setSmsCode("");
            setPinSent(false);
        }
    }, [open, data, phoneOptions]); // eslint-disable-line

    if (!open || !data) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleMoneyInput = (e, field) => {
        handlePriceInput(e);
        const raw = e.target.value;

        const cleaned = raw.replace(/,/g, "");
        const [intRaw, decRaw = ""] = cleaned.split(".");
        const intWithSep = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const dec = decRaw.slice(0, 2);

        const finalVal = dec.length > 0 ? `${intWithSep}.${dec}` : intWithSep || "0";
        e.target.value = finalVal;

        setForm((prev) => ({
            ...prev,
            [field]: finalVal,
        }));
    };

    const handleMoneyBlur = (field) => {
        setForm((prev) => ({
            ...prev,
            [field]: formatMoney(prev[field]),
        }));
    };

    const handleChangePhone = (option) => {
        setSelectedPhone(option);
        setForm((prev) => ({
            ...prev,
            phone: option ? option.label : "",
        }));
    };

    const handleSendPin = async () => {
        if (!reason.trim()) {
            alert("Por favor, describe el motivo del ajuste en el textarea antes de solicitar el PIN.");
            return;
        }

        setSendingPin(true);
        try {
            const token = localStorage.getItem("token");
            const url = `${API_BASE}/register/${safeData.id}/edit/send-pin`;

            const reasonClean = reason.trim().replace(/\s+/g, " ");

            const resp = await axios.post(
                url,
                { reason: reasonClean },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            if (resp.data && resp.data.msg === "success") {
                setPinSent(true);
                setReason(reason.trim().replace(/\s+/g, " "));
                alert("PIN enviado por SMS con la descripción del cambio.");
            } else {
                alert(resp.data?.error || "No se pudo enviar el PIN.");
            }
        } catch (err) {
            console.error(err);
            alert("Error al enviar el PIN.");
        } finally {
            setSendingPin(false);
        }
    };

    const handleSave = async () => {
        const moneyErrors = validateMoneyLogic(form);
        if (moneyErrors.length > 0) {
            alert("Errores encontrados:\n" + moneyErrors.join("\n"));
            return;
        }

        if (!reason.trim()) {
            alert("Por favor, describe el motivo del ajuste en el textarea.");
            return;
        }

        if (!pinSent) {
            alert("Primero debes solicitar y recibir el PIN por SMS.");
            return;
        }

        if (!smsCode.trim()) {
            alert("Ingresa el PIN recibido por SMS.");
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const url = `${API_BASE}/register/${safeData.id}/edit`;

            const resp = await axios.post(
                url,
                {
                    ...form,
                    reason,
                    smsCode,
                    phoneForPin: PIN_PHONE,
                    sedeID: safeData.sedeID,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            if (resp.data && resp.data.msg === "success") {
                onSaved({
                    registers: resp.data.registers || [],
                    multiData: resp.data.multiData || null,
                });
                onClose();
                alert("Registro actualizado correctamente.");
            } else if (resp.data && resp.data.msg === "invalid_pin") {
                alert("PIN incorrecto o vencido.");
            } else {
                alert(resp.data?.error || "No se pudo actualizar el registro.");
            }
        } catch (err) {
            console.error(err);
            const msg =
                err.response?.data?.error ||
                err.response?.data?.msg ||
                err.message ||
                "Error al actualizar el registro.";
            alert(msg);
        } finally {
            setSaving(false);
            toast.success("Actualizado con éxito.", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "colored",
            });
        }
    };

    const handleFMChange = (e) => {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            fm: value,
            fmAmount: value === "0" ? "0.00" : prev.fmAmount,
        }));
    };

    return (
        <div className="fpPopup fpPopupEditReg">
            <div className="fpPopupCover" onClick={onClose}></div>
            <div className="fpClosePopupFM" onClick={onClose}></div>

            <div className="fpPopupInt">
                <div className="fpPopupIntCenter">
                    <div className="fpPopupScroll">
                        <div className="parentFormFm fadeInZoom editRegisterPopup">
                            <h2 className="editRegisterTitle">EDITAR REGISTRO #{safeData.id}</h2>

                            <div className={"registerTable registerTable" + classNameSet}>
                                <table>
                                    <thead>
                                        <tr>
                                            {segmentSet && <th>Fuente</th>}

                                            {!hideEquipoCelular && <th>Equipo Celular</th>}
                                            <th>Precio</th>

                                            <th>DNI</th>
                                            <th>Nombre</th>
                                            <th>Apellido 1</th>
                                            <th>Apellido 2</th>

                                            <th>IMEI</th>
                                            {!hideFM && <th>FM</th>}
                                            {!hideMontoFM && <th>Monto FM</th>}

                                            <th>EMAIL</th>
                                            <th>Celular</th>
                                            <th>Monto Efectivo</th>
                                            <th>Pago Online</th>
                                            <th>Monto Online</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr>
                                            {segmentSet && <td className="sourceTD">{segmentSet}</td>}

                                            {/* Equipo Celular (OCULTO en pago cuota) */}
                                            {!hideEquipoCelular && (
                                                <td width="12%" className="tableWidth">
                                                    <Select
                                                        options={phoneOptions || []}
                                                        value={selectedPhone}
                                                        onChange={handleChangePhone}
                                                        placeholder={"SELECCIONE UN EQUIPO"}
                                                        className="selectForm formItem selectPhone"
                                                        name="phoneName"
                                                    />
                                                </td>
                                            )}

                                            {/* Precio (se mantiene visible; si quieres también deshabilitarlo en cuota, queda así) */}
                                            <td className="withSymbol" width="6%">
                                                <span>S/.</span>
                                                <input
                                                    type="text"
                                                    name="phonePrice"
                                                    value={form.phonePrice}
                                                    onInput={(e) => handleMoneyInput(e, "phonePrice")}
                                                    onBlur={() => handleMoneyBlur("phonePrice")}
                                                    disabled={isCuotaPago}
                                                />
                                            </td>

                                            {/* DNI (NO editable) */}
                                            <td width="6%">
                                                <input
                                                    type="text"
                                                    name="dni"
                                                    value={form.dni}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                            </td>

                                            {/* Nombre (NO editable) */}
                                            <td width="7%">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={form.name}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                            </td>

                                            {/* Apellido 1 (NO editable) */}
                                            <td width="7%">
                                                <input
                                                    type="text"
                                                    name="paternalSur"
                                                    value={form.paternalSur}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                            </td>

                                            {/* Apellido 2 (NO editable) */}
                                            <td width="7%">
                                                <input
                                                    type="text"
                                                    name="maternalSur"
                                                    value={form.maternalSur}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                            </td>

                                            {/* IMEI (visible; si quieres bloquearlo en cuota, queda así) */}
                                            <td width="9%">
                                                <input
                                                    type="text"
                                                    name="imei"
                                                    value={form.imei}
                                                    onChange={handleChange}
                                                    disabled={isCuotaPago}
                                                />
                                            </td>

                                            {/* FM (OCULTO en pago cuota) */}
                                            {!hideFM && (
                                                <td width="4%">
                                                    <select
                                                        name="fm"
                                                        value={form.fm}
                                                        onChange={handleFMChange}
                                                        disabled={isCuotaPago}
                                                    >
                                                        <option value="0">No</option>
                                                        <option value="1">Sí</option>
                                                    </select>
                                                </td>
                                            )}

                                            {/* Monto FM (OCULTO en pago cuota) */}
                                            {!hideMontoFM && (
                                                <td width={"5.5%"} className="withSymbol">
                                                    <span>S/.</span>
                                                    <input
                                                        type="text"
                                                        name="fmAmount"
                                                        value={form.fmAmount}
                                                        onInput={(e) => handleMoneyInput(e, "fmAmount")}
                                                        onBlur={() => handleMoneyBlur("fmAmount")}
                                                        disabled={isCuotaPago || form.fm !== "1"}
                                                    />
                                                </td>
                                            )}

                                            {/* EMAIL */}
                                            <td width="11%">
                                                <input
                                                    type="text"
                                                    name="email"
                                                    value={form.email}
                                                    onChange={handleChange}
                                                />
                                            </td>

                                            {/* Celular */}
                                            <td width="6%">
                                                <input
                                                    type="text"
                                                    name="phoneNumber"
                                                    value={form.phoneNumber}
                                                    onChange={handleChange}
                                                />
                                            </td>

                                            {/* Monto Efectivo */}
                                            <td width="8%" className="withSymbol">
                                                <span>S/.</span>
                                                <input
                                                    type="text"
                                                    name="cashAmount"
                                                    value={form.cashAmount}
                                                    onInput={(e) => handleMoneyInput(e, "cashAmount")}
                                                    onBlur={() => handleMoneyBlur("cashAmount")}
                                                />
                                            </td>

                                            {/* Pago Online */}
                                            <td width="9%">
                                                <select
                                                    name="onlinePay"
                                                    value={form.onlinePay != null ? String(form.onlinePay) : ""}
                                                    onChange={handleChange}
                                                >
                                                    <option value="0">Selecciónar</option>
                                                    <option value="1">Yape</option>
                                                    <option value="5">Culqi Yape</option>
                                                    <option value="2">Tarjeta</option>
                                                    <option value="6">Culqi Tarjeta</option>
                                                    <option value="3">Transferencia</option>
                                                </select>
                                            </td>

                                            {/* Monto Online */}
                                            <td width={"8%"} className="withSymbol">
                                                <span>S/.</span>
                                                <input
                                                    type="text"
                                                    name="onlineAmount"
                                                    value={form.onlineAmount}
                                                    onInput={(e) => handleMoneyInput(e, "onlineAmount")}
                                                    onBlur={() => handleMoneyBlur("onlineAmount")}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* MOTIVO DE LA EDICIÓN */}
                            <div className="editRegisterSection">
                                <label className="formItem editRegFormItem">
                                    <span className="formLabel editRegFormLabel">
                                        Descripción del ajuste / motivo de la edición{" "}
                                        <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <textarea
                                        rows={3}
                                        required
                                        value={reason}
                                        onChange={(e) => {
                                            setReason(e.target.value);
                                            // Si modifica el motivo luego de enviar el PIN, debe solicitar uno nuevo.
                                            if (pinSent) {
                                                setPinSent(false);
                                                setSmsCode("");
                                            }
                                        }}
                                        maxLength={500}
                                        placeholder="Ejemplo: Se corrigió el IMEI y el monto FM por error de digitación."
                                        className="descriptionReg"
                                    />
                                </label>
                            </div>

                            {/* PIN SMS */}
                            <div className="editRegisterSection editRegisterPinSection">
                                <div className="formRow">
                                    <button
                                        type="button"
                                        onClick={handleSendPin}
                                        disabled={sendingPin}
                                        className="btnSendPin"
                                    >
                                        {sendingPin ? "Enviando PIN..." : "Enviar PIN por SMS"}
                                    </button>
                                </div>

                                {pinSent && (
                                    <label className="formItem pinInputLabel">
                                        <span className="formLabel editRegFormLabel">
                                            Ingresa el PIN recibido
                                        </span>
                                        <input
                                            type="text"
                                            value={smsCode}
                                            onChange={(e) => setSmsCode(e.target.value)}
                                            placeholder="Código SMS"
                                        />
                                    </label>
                                )}
                            </div>

                            {/* FOOTER BOTONES */}
                            <div className="editRegisterFooter">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={saving}
                                    className="btnCancelEdit"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving || !pinSent || !smsCode.trim()}
                                    className="btnSaveEdit"
                                >
                                    {saving ? "Guardando..." : "Guardar cambios"}
                                </button>
                            </div>

                            <ToastContainer />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditRegisterModal;
